import {useState} from 'react';

import {SystemHealth as SystemHealthTypes} from '~/modules/api';
import {useGetSystemHealthHistoryQuery} from '~/store/rtkQuery';

import {HealthChart} from './HealthChart';
import {
  RAG_EMOJI,
  fmtNum,
  isMinVolumeCapped,
  isWeekendWindow,
  windowLabel,
} from './health.utils';

const DAYS_OPTIONS = [30, 60, 90, 120];

const RAG_CELL_CLASS: Record<string, string> = {
  green: 'bg-green-100',
  amber: 'bg-amber-100',
  red: 'bg-red-100',
};

/**
 * One-line "system · collection · field → what is counted" under the card
 * stats; click to expand the window semantics and caveats. The text comes
 * from each collector's `source` in luna-firebase, so it stays next to the
 * query it describes.
 */
const MetricSourceLine = ({
  source,
}: {
  source: SystemHealthTypes.MetricSource | undefined;
}) => {
  const [open, setOpen] = useState(false);
  if (!source) return null;
  const details = [
    `window: ${
      source.window ?? 'previous day 08:00 → report date 08:00 Europe/London'
    }`,
    ...(source.caveats ?? []),
  ];
  return (
    <div className="text-[11px] leading-snug text-gray-500 mb-1">
      <button
        type="button"
        className="text-left cursor-pointer hover:text-gray-700"
        title={open ? 'hide details' : 'show window + caveats'}
        onClick={() => setOpen(v => !v)}>
        <span className="font-semibold text-gray-700">{source.system}</span>{' '}
        {source.from} → {source.counts} {open ? '▾' : '▸'}
      </button>
      {open && (
        <ul className="list-disc pl-4 mt-0.5 space-y-0.5">
          {details.map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MetricCard = ({
  metric,
  fromZero,
}: {
  metric: SystemHealthTypes.SystemHealthHistoryMetric;
  fromZero: boolean;
}) => {
  const numeric = metric.points.filter(p => typeof p.value === 'number');
  const vals = numeric.map(p => p.value as number);
  const latest = vals.length ? vals[vals.length - 1] : null;
  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  const latestStatus = metric.points.length
    ? metric.points[metric.points.length - 1].status
    : 'unavailable';

  const reds = metric.points.filter(p => p.status === 'red').length;
  const ambers = metric.points.filter(p => p.status === 'amber').length;
  const capped = metric.points.filter(p =>
    isMinVolumeCapped(p, metric.thresholds),
  ).length;
  const errors = metric.points.filter(p => p.error);

  let ragCounts: string;
  if (reds || ambers) {
    const parts = [];
    if (reds) parts.push(`🔴 ${reds} day${reds > 1 ? 's' : ''} (sys alert)`);
    if (ambers) parts.push(`🟠 ${ambers} day${ambers > 1 ? 's' : ''}`);
    ragCounts =
      `flagged: ${parts.join(' · ')}` +
      (capped ? ` (${capped} red→amber via min-volume)` : '');
  } else {
    ragCounts = 'no amber/red days at these thresholds';
  }

  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl px-4 pt-3 pb-2 ${
        metric.disabled ? 'opacity-55' : ''
      }`}>
      <h2 className="text-sm font-title flex justify-between items-baseline mb-0.5">
        <span>
          {RAG_EMOJI[latestStatus] ?? ''} {metric.label}
        </span>
        <span className="text-lg">
          {latest === null ? '—' : fmtNum(latest)}
        </span>
      </h2>
      <div className="text-xs text-gray-500 mb-1">
        avg {fmtNum(Math.round(avg))} · min{' '}
        {vals.length ? fmtNum(Math.min(...vals)) : '—'} · max{' '}
        {vals.length ? fmtNum(Math.max(...vals)) : '—'} · {vals.length}/
        {metric.points.length} days
      </div>
      <div className="text-xs mb-1">
        {metric.disabled
          ? '⛔ disabled via perMetric config — excluded from the daily health check'
          : ragCounts}
      </div>
      <MetricSourceLine source={metric.source} />
      <HealthChart
        points={metric.points}
        thresholds={metric.thresholds}
        fromZero={fromZero}
      />
      {errors.length > 0 && (
        <div className="text-xs text-orange-700 mt-0.5">
          ⚠ {errors.length} day(s) errored: {errors[0].error}
        </div>
      )}
    </div>
  );
};

export const SystemHealth = () => {
  const [days, setDays] = useState(30);
  const [fromZero, setFromZero] = useState(true);
  const [showConfig, setShowConfig] = useState(false);

  const {data, error, isLoading, isFetching, refetch} =
    useGetSystemHealthHistoryQuery({days});

  if (error) {
    return (
      <div className="p-4">
        <div role="alert" className="alert alert-error">
          <span>Error: {error.message}</span>
        </div>
        <button className="btn btn-primary mt-4" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const metrics = data?.metrics ?? [];

  return (
    <div className="p-4 pb-10">
      <div className="flex flex-wrap items-baseline gap-3 mb-1">
        <h1 className="text-lg font-title">📊 System Health</h1>
        {data && (
          <span className="text-sm text-gray-500">
            <b className="text-black">{data.dates.length}</b> days
            {data.from && data.to && (
              <>
                {' '}
                ({data.from} → {data.to})
              </>
            )}{' '}
            · anchor <b className="text-black">{data.windowAnchor}</b>
          </span>
        )}
        {(isLoading || isFetching) && (
          <span className="loading loading-dots loading-sm"></span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
        <label className="flex items-center gap-1.5">
          days
          <select
            className="select select-bordered select-xs"
            value={days}
            onChange={e => setDays(parseInt(e.target.value, 10))}>
            {DAYS_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="checkbox checkbox-xs"
            checked={fromZero}
            onChange={e => setFromZero(e.target.checked)}
          />
          y-axis from zero
        </label>
        <span>
          <span className="inline-block w-3 h-3 align-[-2px] mr-1 bg-[#fff3e6] border border-[#f0ddc4]" />
          weekend window (label date is the morning after: each day covers
          previous day 08:00 → label date 08:00 UK)
        </span>
        <button
          className="btn btn-ghost btn-xs"
          onClick={() => setShowConfig(v => !v)}>
          {showConfig ? 'hide' : 'view'} RAG setup
        </button>
      </div>

      {showConfig && data && (
        <pre className="mb-3 px-4 py-3 bg-white border border-gray-200 rounded-xl text-xs overflow-x-auto">
          {'── code defaults (rag.ts DEFAULT_THRESHOLDS) ──\n' +
            JSON.stringify(data.defaults, null, 2) +
            '\n\n── live overrides (RTDB sysadmin/systemHealth) ──\n' +
            JSON.stringify(data.config, null, 2) +
            `\n\nRAG activates after ${data.minSnapshotsForRag} snapshots per metric.`}
        </pre>
      )}

      {isLoading && !data && (
        <div className="py-10 text-gray-500">
          <span className="loading loading-dots loading-md"></span>
        </div>
      )}

      {data && metrics.length > 0 && (
        <>
          <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
            {metrics.map(metric => (
              <MetricCard
                key={metric.key}
                metric={metric}
                fromZero={fromZero}
              />
            ))}
          </div>

          <h2 className="text-base font-title mt-8 mb-2">
            Raw values (for manual comparison)
          </h2>
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
            <table className="table table-xs table-pin-rows table-pin-cols min-w-[900px]">
              <thead>
                <tr>
                  <th>report date</th>
                  {metrics.map(metric => (
                    <th key={metric.key} title={metric.label}>
                      {metric.label.replace(/ \(.*\)/, '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.dates.map((date, di) => (
                  <tr
                    key={date}
                    className={isWeekendWindow(date) ? 'bg-[#fff3e6]' : ''}>
                    <td title={windowLabel(date)}>{date}</td>
                    {metrics.map(metric => {
                      const point = metric.points[di];
                      if (!point || point.value === null) {
                        const reason = point?.error
                          ? point.error
                          : 'no value recorded';
                        return (
                          <td
                            key={metric.key}
                            className="text-gray-400 italic"
                            title={reason}>
                            {point?.error ? 'ERR' : '—'}
                          </td>
                        );
                      }
                      const delta =
                        typeof point.deltaPct === 'number'
                          ? ` · ${point.deltaPct >= 0 ? '+' : ''}${Math.round(
                              point.deltaPct,
                            )}% vs baseline`
                          : '';
                      return (
                        <td
                          key={metric.key}
                          className={`text-right tabular-nums ${
                            RAG_CELL_CLASS[point.status] ?? ''
                          } ${
                            point.value === 0
                              ? 'text-red-600 font-semibold'
                              : ''
                          }`}
                          title={`${point.status}${delta}${
                            point.backfilled ? ' · backfilled' : ''
                          }`}>
                          {fmtNum(point.value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data && data.dates.length === 0 && (
        <div className="py-10 text-gray-500">
          No snapshots yet — the daily health run (or a backfill) hasn't written
          to systemHealthSnapshots.
        </div>
      )}
    </div>
  );
};
